/**
 * 多版本菜单 B6（老扫码站 U-B6-2 review 第 1 轮 F2）：购物车「标红」（`store.state.expireProductList`）的纯函数。**没有任何 import** ——
 * `store/app.js` 也要用（加购时撤掉标记），不能经 `@/utils/language` 绕回 store，形成循环引用。
 *
 * 标记的形状 `{id: venueProductId, type, source?}`：
 * - `type`：0 = 这件现在不能买（`##0##` 那条老路同一个值）；1 = 缺库存（`##11##`）。
 * - `source`（这一批新加，只有本功能写的标记才有）：
 *   - `'fallback'`：下单被拒、后端说这一单的菜单已经不适用（信封 `menuFallback: true`）—— 后端是按**主菜单**判的；
 *   - `'order'`：下单被拒、带 id、不是回落（就是普通的「这件没有了」，行为与 `##0##` 标的一样）；
 *   - `'menu'`：本地核对（D97）发现这一行不在当前菜单的商品表上。
 *   没有 `source` 的标记（`##0##` / `##11##` 那两条老路、以及上线前存下来的老购物车）**本功能从不删、从不改**。
 *
 * 「在不在当前菜单上」自成一维（review 第 3 轮 H2）：同一个 id 可以**既有** `menu` 标记、**又有**别的标记（回落 / 普通拒单 / 老标记 / 缺库存）。
 * 合并、去重都按（id，是不是 menu）各算各的：一条不会把另一维的那条顶掉 —— 不然改取餐时间（只留 menu，`keepMenuMarks`）或
 * 「菜单刚更新」撤掉回落标记之后，那件不在当前菜单上的东西就没有标记了，而建车菜单已经是当前菜单、不会再核对。
 * 同一个 id 有几条时，行上的说明照第一条算数的（`markOfLine`）：已有的在前、后加的接在后面，所以缺库存 / 老标记那条的说明不变。
 */

/** 「商品不能买了」的 type（`##0##` 那条路同一个值；1 是缺库存） */
export const EXPIRED_MARK_TYPE = 0

export const MARK_SOURCE_FALLBACK = 'fallback'
export const MARK_SOURCE_ORDER = 'order'
export const MARK_SOURCE_MENU = 'menu'

const MENU_CHANGE_SOURCES = [MARK_SOURCE_FALLBACK, MARK_SOURCE_ORDER, MARK_SOURCE_MENU]

const idOf = (mark) => (mark && (typeof mark.id === 'string' || typeof mark.id === 'number') ? String(mark.id) : '')
const listOf = (marks) => (Array.isArray(marks) ? marks : [])
/** 合并 / 去重用的键（H2）：`menu` 标记一维、别的标记一维，同一个 id 两维各一条 */
const keyOf = (mark) => `${mark && mark.source === MARK_SOURCE_MENU ? 'menu' : 'other'}:${idOf(mark)}`

/** 这条标记是不是本功能（菜单变了 / 带 id 的拒单）写的：购物车行的说明一律用「这件现在没有了」 */
export function isMenuChangeMark(mark) {
  return !!mark && MENU_CHANGE_SOURCES.includes(mark.source)
}

/**
 * 一条标记算不算在购物车的这一行上（review 第 2 轮 G3）。标记按商品 id 记，同一件商品可以既有顾客自己加的行、又有店家强塞的行
 * （`disableOperation`，`spSettings.cartAddProduct`，删除键是藏着的）。本功能写的标记（带 source）**不算**在强塞行上 ——
 * 不然顾客删掉自己那一行之后，强塞行还标着、又删不掉，下单按钮永远灰。
 * 没有 source 的老标记（`##0##` / `##11##` / 上线前存下来的）照今天：id 严格相等就算（以前各处都是 `expire.id === item.id`）。
 * 用到标记的地方都按这条：购物车页 / 合单购物车的 `hasExpireProduct`、购物车行 `Items isExpire`。
 */
export function markAppliesToLine(mark, line) {
  if (!mark || !line || mark.id !== line.id) return false
  return !(isMenuChangeMark(mark) && line.disableOperation)
}

/** 这一行身上的标记：第一条算数的那条（没有 ⇒ undefined，与以前 `expireProductList.find(...)` 同一个返回） */
export function markOfLine(marks, line) {
  return listOf(marks).find(mark => markAppliesToLine(mark, line))
}

/**
 * 合单购物车里一条标记算不算在这一行上（review 第 3 轮 H3）：`menu` 标记**不算** —— 合单一律主菜单、从不做 D97（D98①），
 * 那是单店那次本地核对留下的；别的标记与 `markAppliesToLine` 一样（含强塞行的规则）。用在合单购物车 `hasExpireProduct` 与合单行 `Items isExpire`。
 */
export function markAppliesInSubGroup(mark, line) {
  return !!mark && mark.source !== MARK_SOURCE_MENU && markAppliesToLine(mark, line)
}

/** 合单行身上的标记（跳过 `menu` 标记的 `markOfLine`） */
export function subGroupMarkOfLine(marks, line) {
  return listOf(marks).find(mark => markAppliesInSubGroup(mark, line))
}

/**
 * 合并标记：已有的（含缺库存 type 1、没有 source 的老标记）原样留着、不改 source，只追加还没有的；不改调用方的数组。
 * 「还没有」按（id，是不是 menu）算（H2）：已有 menu 标记的 id 照样能加拒单的标记，反过来也一样；同一维里同一个 id 只留先来的那条。
 */
export function mergeExpireMarks(existing, marks) {
  const list = listOf(existing).slice()
  const seen = new Set(list.map(keyOf))
  listOf(marks).forEach(mark => {
    if (!mark || seen.has(keyOf(mark))) return
    seen.add(keyOf(mark))
    list.push(mark)
  })
  return list
}

/**
 * 这次合并**新加上**的 id（同一维里已经有标记的 id 不算新加 —— 之后撤标记只撤这些，不会把别人标的连带撤掉；
 * 只有 menu 标记的 id 加上拒单的标记算新加，H2。撤回落标记只撤 `source: 'fallback'` 那一条，menu 那条另由 D97 重判管）
 */
export function newMarkIds(existing, marks) {
  const seen = new Set(listOf(existing).map(keyOf))
  const added = []
  listOf(marks).forEach(mark => {
    const id = idOf(mark)
    if (!id || seen.has(keyOf(mark))) return
    seen.add(keyOf(mark))
    added.push(id)
  })
  return added
}

/**
 * D97 按一份菜单重判（venue-home / 购物车页 / 「菜单刚更新」之后）：先撤掉这份菜单上**有**的商品的 `source: 'menu'` 标记，
 * 再给不在上面的行补 `source: 'menu'` 标记（已有 menu 标记的 id 不重复加；同一个 id 有别的标记也照样补 —— H2，接在后面）。
 * 别的标记一条不碰（不撤、不改、位置不动）。
 * @param {Array} existing 现在的标记
 * @param {Set<String>} productIds 这份菜单上的商品 id
 * @param {String[]} offIds 购物车里不在这份菜单上的行（`linesOffMenu`）
 */
export function reevaluateMenuMarks(existing, productIds, offIds) {
  const kept = listOf(existing).filter(mark => !(mark && mark.source === MARK_SOURCE_MENU && productIds && productIds.has(idOf(mark))))
  return mergeExpireMarks(kept, listOf(offIds).map(id => ({ id: String(id), type: EXPIRED_MARK_TYPE, source: MARK_SOURCE_MENU })))
}

/**
 * 「菜单刚更新」之后（owner 托管的 P2 规则）：这次拒单**新加上**的回落标记里，重新解析出的菜单卖、而且在那份菜单上能下单的，撤掉。
 * 只撤 `source: 'fallback'`、type 0、在 `addedIds` 里、在 `orderableIds` 里的；别的一条不碰。
 */
export function unmarkOrderableFallback(existing, addedIds, orderableIds) {
  const added = new Set(listOf(addedIds).map(String))
  return listOf(existing).filter(mark => !(
    mark &&
    mark.source === MARK_SOURCE_FALLBACK &&
    mark.type === EXPIRED_MARK_TYPE &&
    added.has(idOf(mark)) &&
    orderableIds && orderableIds.has(idOf(mark))
  ))
}

/**
 * `##<n>##` 拒单弹窗（`requestMixin.js _addOrderErrorDialog`）今天整份替换标记：替换照旧（新列表原样在前），再把已有的 `source: 'menu'` 标记
 * 带上 —— 不然后端的一次别的回答会把「不在当前菜单上」的行悄悄放行。新列表里也有的 id 照样带上（H2：两维各算各的，
 * 缺库存那条在前、行上照旧说缺库存；改取餐时间清掉缺库存那条之后 menu 那条还在）。
 */
export function withMenuMarksCarried(existing, replacement) {
  const list = listOf(replacement).slice()
  const seen = new Set(list.map(keyOf))
  listOf(existing).forEach(mark => {
    if (!mark || mark.source !== MARK_SOURCE_MENU || seen.has(keyOf(mark))) return
    seen.add(keyOf(mark))
    list.push(mark)
  })
  return list
}

/** 加购时（从当前菜单的分类里加回这件）：撤掉这件的 `menu` / `fallback` 标记 —— 菜单上显示它能买。没有 source 的、type 1 的不碰 */
export function dropMarksOnReAdd(existing, productId) {
  const id = productId === undefined || productId === null ? '' : String(productId)
  if (!id) return listOf(existing)
  return listOf(existing).filter(mark => !(
    mark &&
    idOf(mark) === id &&
    mark.type === EXPIRED_MARK_TYPE &&
    (mark.source === MARK_SOURCE_MENU || mark.source === MARK_SOURCE_FALLBACK)
  ))
}

/**
 * 改了取餐 / 送餐时间（购物车 `deliveryInfo` 监听，review 第 2 轮 G4）：今天整份清掉标红（那些是「这个时间不行」的判断）；
 * 本地核对的「不在当前菜单上」（`source: 'menu'`）跟时间无关（解析出的菜单没变），留着 —— 不然那几行不在菜单上的东西照样能结账，下单再被后端拒一次。
 */
export function keepMenuMarks(existing) {
  return listOf(existing).filter(mark => !!mark && mark.source === MARK_SOURCE_MENU)
}

/** 购物车里这家店的行（不算店家强塞的 disableOperation 行，与 `addNewProductToCart` 记建车菜单同口径） */
export function cartLinesOf(productList, venueId) {
  if (!Array.isArray(productList)) return []
  return productList.filter(line => line && line.venueId === venueId && !line.disableOperation)
}

/**
 * 这一行能不能拿菜单的商品表来判：是从菜单分类里加的（商品页带着分类 id 加进来的主商品）。
 * 推荐 / AI 推荐 / 套餐升级 / 没有分类来源的行**不判**：这些商品可以不在任何分类里（后端对没有分类的商品放行，
 * `OrderItemForm::productOutsideMenu`），拿商品表去判会把能买的标成不能买、顾客白删；交给下单时后端判（被拒就按 id 标）。
 */
export function isMenuCategoryLine(line, venueId) {
  if (!line || line.venueId !== venueId || line.disableOperation) return false
  if (line.recommendFromProductId || line.aiRecommendFromProductId || line.upgradeFromProductId) return false
  return !!line.productGroupId
}

/**
 * F4：这件商品在购物车里**只有**店家强塞的行（`disableOperation`，`spSettings.cartAddProduct`）—— 顾客删不掉，标了下单按钮就永远灰着。
 * 这种 id 不标（只弹今天的提示）。车里根本没有这件的行 ⇒ false（照今天标，标了也不影响任何一行）。
 */
export function isForcedOnlyId(productList, venueId, productId) {
  const id = String(productId)
  const lines = (Array.isArray(productList) ? productList : []).filter(line => line && line.venueId === venueId && String(line.id) === id)
  return lines.length > 0 && lines.every(line => !!line.disableOperation)
}
